import React, { useEffect, useState } from 'react';
import { Button, Checkbox, Empty, List, message, Popconfirm, Tooltip } from 'antd';
import { fieldNameMap, requiredFieldList, systemFieldList } from '@/components/DynamicForm/utils';
import { v4 as uuid } from 'uuid';
import CustomFieldConfigModal from '@/components/DynamicForm/config/CustomFieldConfigModal';
import { isEmpty } from 'lodash';
import { TCustomField, TRequirementFieldConfig } from '@/components/DynamicForm/model';
import {
  DeleteOutlined,
  FormOutlined,
  PlusSquareOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { removeCustomField } from '@/services/DynamicFormRequest';
import cx from 'classnames';

export default function FieldList({
  campId,
  customFieldList = [],
  requiredFields = requiredFieldList,
  systemFields = systemFieldList,
  defaultFields = [], // 该参数不为空时，自动注入默认字段
  listCustomField,
  fields,
  setFields,
}: {
  campId: string;
  customFieldList: TCustomField[];
  requiredFields?: any[];
  systemFields?: any[];
  defaultFields?: any[]; // 该参数不为空时，自动注入默认字段
  listCustomField: () => void;
  fields: TRequirementFieldConfig[];
  setFields: (fields: TRequirementFieldConfig[]) => void;
}) {
  const [customModalVisible, setCustomModalVisible] = useState(false);
  const [selectedCustomField, setSelectedCustomField] = useState<TCustomField>();
  useEffect(() => {
    if (!defaultFields || isEmpty(defaultFields)) {
      return;
    }

    const currentFields = fields || [];
    defaultFields.forEach((defaultFields) => {
      const newField = {
        id: `custom-${uuid()}`,
        fieldType: defaultFields.fieldType,
        displayed: true,
        required: false,
      };
      currentFields.push(newField);
    });
    setFields(currentFields);
  }, [defaultFields]);

  const onAddFieldSuccess = () => {
    setCustomModalVisible(false);
    listCustomField();
  };

  const onSystemFieldChecked = (event, typeField) => {
    const typeFieldList = fields || [];
    const checked = event.target.checked;

    if (checked) {
      if (!typeField) {
        const newTypeField = {
          id: `custom-${uuid()}`,
          fieldType: event.target.value,
          displayed: true,
          required: false,
        };

        const newFieldList = typeFieldList.concat([newTypeField]);
        setFields(newFieldList);
      }
    } else {
      if (typeField) {
        const newTypeFieldList = typeFieldList.filter(({ id }) => id !== typeField.id);
        setFields(newTypeFieldList);
      }
    }
  };

  const onCustomFieldChecked = (event, customField, typeField) => {
    const typeFieldList = fields || [];
    const checked = event.target.checked;

    if (checked) {
      if (!typeField) {
        const newTypeField = {
          id: `custom-${uuid()}`,
          fieldType: 'custom',
          displayed: true,
          required: false,
          customFieldEntityId: customField.id,
          customField,
        };

        const newFieldList = typeFieldList.concat([newTypeField]);
        setFields(newFieldList);
      }
    } else {
      if (typeField) {
        const newTypeFieldList = typeFieldList.filter(({ id }) => id !== typeField.id);
        setFields(newTypeFieldList);
      }
    }
  };

  const onCustomFieldEdit = (customField: TCustomField) => {
    setSelectedCustomField(customField);
    setCustomModalVisible(true);
  };

  const onCustomFieldDelete = (customField: TCustomField) => {
    if (!customField || !customField.id) {
      return;
    }

    removeCustomField(customField.id)
      .then(() => {
        listCustomField();
        message.success('删除成功');
      })
      .catch(() => {
        message.error('删除失败');
      });
  };

  const renderSystemField = (systemField) => {
    const fieldName = fieldNameMap[systemField];
    const typeFieldList = fields || [];
    const typeField = typeFieldList.find(({ fieldType }) => fieldType === systemField);

    return (
      <Checkbox
        checked={typeField && typeField?.displayed}
        value={systemField}
        disabled={requiredFields.includes(systemField)}
        onChange={(e) => {
          onSystemFieldChecked(e, typeField);
        }}
      >
        {fieldName}
      </Checkbox>
    );
  };

  const renderCustomField = (customField: TCustomField) => {
    const typeFieldList = fields || [];
    // @ts-ignore
    const typeField = typeFieldList.find(
      ({ customFieldEntityId }) => customFieldEntityId === customField.id,
    );

    return (
      <Checkbox
        checked={typeField && typeField?.displayed}
        value={customField.id}
        onChange={(e) => {
          onCustomFieldChecked(e, customField, typeField);
        }}
      >
        <Tooltip placement="topLeft" title={customField.name}>
          {customField.name}
        </Tooltip>
        <Button type="link" size="small">
          <FormOutlined
            className={cx('text-[#5A5A5A]')}
            onClick={() => {
              onCustomFieldEdit(customField);
            }}
          />
          <Popconfirm
            overlayClassName="custom-field-pop"
            title={`确定删除「${customField.name}」吗？`}
            description="字段删除后无法恢复，请谨慎操作"
            icon={<QuestionCircleOutlined />}
            cancelText="取消"
            okText="删除"
            okButtonProps={{ type: 'primary', danger: true }}
            onConfirm={() => {
              onCustomFieldDelete(customField);
            }}
          >
            <DeleteOutlined className={cx('ml-[10px]', 'text-[#5A5A5A]')} />
          </Popconfirm>
        </Button>
      </Checkbox>
    );
  };

  return (
    <div className="type-field-list-wrapper">
      <div className="list-header">配置字段</div>
      <div className="list-body">
        <div className="system-field-list-wrapper">
          <div className="list-subtitle">内置字段</div>
          <div className="list-content">
            <List
              dataSource={systemFields}
              renderItem={renderSystemField}
              className="system-field-list"
            />
          </div>
        </div>
        <div className="custom-field-list-wrapper">
          <div className="list-subtitle">
            自定义字段
            <Tooltip placement="top" title="添加自定义字段">
              <Button
                icon={<PlusSquareOutlined />}
                size="small"
                type="link"
                onClick={() => {
                  setCustomModalVisible(true);
                  setSelectedCustomField(undefined);
                }}
              >
                添加字段
              </Button>
            </Tooltip>
          </div>
          <div className="list-content">
            <List
              dataSource={customFieldList}
              renderItem={renderCustomField}
              className="custom-field-list"
              locale={{
                emptyText: (
                  <Empty
                    size="normal"
                    description={
                      <span>
                        暂无自定义字段,
                        <Button
                          type="link"
                          onClick={() => {
                            setCustomModalVisible(true);
                          }}
                          style={{ padding: 0 }}
                        >
                          立即创建
                        </Button>
                      </span>
                    }
                  />
                ),
              }}
            />
          </div>
        </div>
      </div>
      <CustomFieldConfigModal
        open={customModalVisible}
        campId={campId}
        customField={selectedCustomField}
        onSuccess={onAddFieldSuccess}
        onClose={() => {
          setCustomModalVisible(false);
        }}
      />
    </div>
  );
}
